/**
 * CastRail — one lamp per voice on the stage edge; the lit one is who
 * your next line goes to.
 *
 * The artifact is the cue lamp. `DESIGN_SPEC.md` §2 already assigns
 * `--brass-cue` to "active voice indicator, focused cue lamp" and
 * `--brass-tarnish` to "dialled-back brass for inactive cue lamps", and
 * the wire type carrying the cast is called `VoiceLamp`. So the vocabulary
 * for this control was chosen before the control existed; a dropdown
 * would have been the generic answer and maps to nothing on a stage.
 *
 * **The active state is a lit dot, not a filled brass button.** That was
 * loop 1's finding and it is only visible in situ: the bottom bar already
 * holds `send` and `send cue` in filled brass, so a third filled-brass
 * element left three things in one bar all claiming to be the primary
 * action. Brass fill belongs to actions. A lamp is state, so it reads as
 * light -- a bright dot with a lamp-pool halo on a riser surface -- and
 * the bar keeps one primary action.
 *
 * Ambition level 2 (`.claude/design-context.md`): honest confirmation and
 * failure, done well. The lamp does not animate beyond a token-timed
 * colour settle, because the phase's level-4 moments are elsewhere and a
 * rail that performs would compete with them.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";

import { castRailCopy } from "./CastRail.copy";

export interface CastMember {
  readonly voiceId: string;
  readonly name: string;
}

export interface CastRailProps {
  readonly cast: readonly CastMember[];
  /** Whose lamp is lit: the voice the next typed line is addressed to. */
  readonly addressed: string;
  readonly onAddress: (voiceId: string) => void;
  readonly onCreate: (sentence: string) => void;
  /** True between sending `client.create-character` and the cast coming back. */
  readonly creating?: boolean;
  /** Set when the server refused the sentence. Cleared by the next attempt. */
  readonly createError?: string | null;
  /**
   * The room is at `MAX_CAST`. Distinct from `disabled` on purpose: these
   * were one `canCreate` flag, and the state matrix caught it saying "the
   * room is full" to a visitor whose socket was merely reconnecting. Two
   * reasons collapsed into one message is a message that is false half
   * the time, and the half it is false in is the half where the visitor
   * is already having a bad time.
   */
  readonly castFull?: boolean;
  /** The socket cannot carry a creation right now. Offers nothing rather
   * than offering something that would fail. */
  readonly disabled?: boolean;
}

export function CastRail({
  cast,
  addressed,
  onAddress,
  onCreate,
  creating = false,
  createError = null,
  castFull = false,
  disabled = false,
}: CastRailProps): ReactElement {
  const [composing, setComposing] = useState<boolean>(false);
  // Both the in-flight state and the refusal are rendered only inside the
  // composer, so either one arriving while it is shut would be silent:
  // the visitor asked for something, nothing happened, and nothing said
  // why. Deriving `open` from them makes invisible-progress and
  // invisible-failure unrepresentable rather than merely unlikely.
  const open = composing || creating || createError !== null;
  const [sentence, setSentence] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the field when it opens. A one-line composer the visitor has to
  // click into is a two-step affordance pretending to be one.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Close on success. `creating` going false with no error is the socket
  // saying the cast changed, which is the only honest close signal --
  // closing optimistically on submit would hide a refusal behind a
  // composer that had already gone away.
  const wasCreating = useRef<boolean>(false);
  useEffect(() => {
    if (wasCreating.current && !creating && createError === null) {
      setComposing(false);
      setSentence("");
    }
    wasCreating.current = creating;
  }, [creating, createError]);

  const submit = useCallback((): void => {
    const text = sentence.trim();
    if (text.length === 0 || creating) return;
    onCreate(text);
  }, [sentence, creating, onCreate]);

  return (
    <div
      data-testid="cast-rail"
      style={{ display: "flex", flexDirection: "column", gap: "0.375rem", flexShrink: 0 }}
    >
      <div
        role="radiogroup"
        aria-label={castRailCopy.railLabel}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.125rem",
          padding: "0.125rem",
          border: "1px solid var(--ledger-line)",
          borderRadius: "var(--radius)",
          background: "var(--stage-carbon)",
        }}
      >
        {cast.map((member) => {
          const lit = member.voiceId === addressed;
          return (
            <button
              key={member.voiceId}
              type="button"
              role="radio"
              aria-checked={lit}
              data-testid={`cast-lamp-${member.voiceId}`}
              data-lit={lit ? "true" : "false"}
              title={castRailCopy.lampTitle(member.name)}
              onClick={(): void => onAddress(member.voiceId)}
              className="font-plex-mono"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                // Riser, not brass. See the header comment: the bar's
                // filled brass is spoken for by `send` and `send cue`.
                background: lit ? "var(--stage-riser)" : "transparent",
                // AA on both grounds: paper-cream on riser is well clear,
                // and the dim tone is the spec's own secondary text token
                // rather than a faded brass that would fail on carbon.
                color: lit ? "var(--paper-cream)" : "var(--paper-cream-dim)",
                border: "1px solid",
                borderColor: lit ? "var(--measure-line)" : "transparent",
                borderRadius: "var(--radius)",
                padding: "0.25rem 0.55rem",
                fontSize: "0.781rem",
                cursor: "pointer",
                // Colour settle only. Reduced-motion safe because the
                // property is colour, not transform or opacity, and the
                // duration is below the threshold where motion reads as
                // movement. Nothing here travels.
                transition: "background-color 120ms ease-out, color 120ms ease-out",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: "0.4375rem",
                  height: "0.4375rem",
                  borderRadius: "50%",
                  display: "block",
                  background: lit ? "var(--brass-strike)" : "var(--brass-tarnish)",
                  // The lamp pool falls on the lit dial and nowhere else.
                  boxShadow: lit ? "0 0 0 3px var(--lamp-pool)" : "none",
                }}
              />
              {member.name}
            </button>
          );
        })}

        {!castFull && !disabled && !open ? (
          <button
            type="button"
            data-testid="cast-add"
            onClick={(): void => setComposing(true)}
            className="font-plex-mono"
            style={{
              background: "transparent",
              color: "var(--paper-cream-dim)",
              border: 0,
              borderLeft: cast.length > 0 ? "1px solid var(--ledger-line)" : 0,
              padding: "0.25rem 0.55rem",
              marginLeft: "0.125rem",
              fontSize: "0.781rem",
              cursor: "pointer",
            }}
          >
            {castRailCopy.addLabel}
          </button>
        ) : null}

        {castFull && !open ? (
          <span
            data-testid="cast-full"
            className="font-plex-mono"
            style={{
              // AA: `--paper-cream-faint` measures 3.7:1 on this ground.
              color: "var(--paper-cream-dim)",
              padding: "0.25rem 0.55rem",
              borderLeft: "1px solid var(--ledger-line)",
              fontSize: "0.781rem",
            }}
          >
            {castRailCopy.full}
          </span>
        ) : null}
      </div>

      {open ? (
        <form
          data-testid="cast-composer"
          onSubmit={(event): void => {
            event.preventDefault();
            submit();
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.3rem",
            padding: "0.5rem",
            background: "var(--rehearsal-taupe)",
            border: "1px solid var(--measure-line)",
            borderRadius: "var(--radius)",
            // Bounded so the composer cannot push the note input out of
            // the bar on a narrow desk.
            maxWidth: "26rem",
          }}
        >
          <label
            htmlFor="cast-sentence"
            className="font-plex-mono"
            style={{ color: "var(--paper-cream-dim)", fontSize: "0.781rem" }}
          >
            {castRailCopy.addTitle}
          </label>
          <div style={{ display: "flex", gap: "0.35rem" }}>
            <input
              id="cast-sentence"
              ref={inputRef}
              type="text"
              data-testid="cast-sentence"
              aria-label={castRailCopy.addInputLabel}
              placeholder={castRailCopy.addPlaceholder}
              value={sentence}
              maxLength={400}
              disabled={creating}
              onChange={(event): void => setSentence(event.target.value)}
              onKeyDown={(event): void => {
                if (event.key === "Escape") setComposing(false);
              }}
              className="font-plex-mono"
              style={{
                flex: 1,
                minWidth: 0,
                background: "var(--stage-pit)",
                color: "var(--paper-cream)",
                border: "1px solid var(--measure-line)",
                borderRadius: "var(--radius)",
                padding: "0.35rem 0.5rem",
                fontSize: "0.781rem",
              }}
            />
            <button
              type="submit"
              data-testid="cast-submit"
              disabled={creating || sentence.trim().length === 0}
              className="font-plex-mono"
              style={{
                background:
                  creating || sentence.trim().length === 0
                    ? "var(--rehearsal-taupe)"
                    : "var(--brass-cue)",
                color:
                  creating || sentence.trim().length === 0
                    ? "var(--paper-cream-dim)"
                    : "var(--score-ink)",
                border: "1px solid var(--measure-line)",
                borderRadius: "var(--radius)",
                padding: "0.35rem 0.7rem",
                fontSize: "0.781rem",
                cursor:
                  creating || sentence.trim().length === 0 ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {creating ? castRailCopy.working : castRailCopy.addSubmit}
            </button>
          </div>

          {/* Directly under the field it refers to, above the one-tap
              starts. Placed last it sat below the cancel, three controls
              away from the input whose contents were refused. Measured
              5.0:1 on this ground, so it clears AA as normal text. */}
          {createError !== null ? (
            <p
              role="alert"
              data-testid="cast-error"
              className="font-plex-mono"
              style={{
                margin: 0,
                color: "var(--dynamic-forte)",
                fontSize: "0.75rem",
              }}
            >
              {createError}
            </p>
          ) : null}

          {/* The two one-tap starts. A blank field is where a demo ends
              for a visitor who does not know what they are allowed to
              type, and these cost nothing to offer. Hidden while the
              call is in flight so they cannot queue a second one. */}
          {!creating ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
              {castRailCopy.examples.map((example) => (
                <button
                  key={example}
                  type="button"
                  data-testid="cast-example"
                  onClick={(): void => setSentence(example)}
                  className="font-plex-mono"
                  style={{
                    background: "transparent",
                    color: "var(--paper-cream-dim)",
                    border: "1px solid var(--ledger-line)",
                    borderRadius: "var(--radius)",
                    padding: "0.2rem 0.45rem",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {example}
                </button>
              ))}
              <button
                type="button"
                data-testid="cast-cancel"
                onClick={(): void => setComposing(false)}
                className="font-plex-mono"
                style={{
                  background: "transparent",
                  // AA again: a 12px cancel is normal text, not large.
                  color: "var(--paper-cream-dim)",
                  border: 0,
                  padding: "0.2rem 0.45rem",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                }}
              >
                {castRailCopy.addCancel}
              </button>
            </div>
          ) : null}

        </form>
      ) : null}
    </div>
  );
}

export default CastRail;
